//@ts-check
/**
 * LOD level mapping utilities derived from {@link ./RcsConstants.js RcsConstants}.
 */

import {
  LOD_MAPPING_MODE,
  DIRECT_MODE_LEVELS_IN_LOD0,
  RCS_LOD_THRESHOLDS } from
'./RcsConstants.js';

/**
 * Range descriptor returned by {@link getLodRange}.
 * In 'threshold' mode, `startPoint`/`endPoint` are populated.
 * In 'direct' mode, `startLevel`/`endLevel` are populated.
 * @typedef {Object} LodRange
 * @property {number} [startPoint] - Start of cumulative point range (threshold mode)
 * @property {number} [endPoint]   - End of cumulative point range (threshold mode)
 * @property {number} [startLevel] - First source octree level (direct mode)
 * @property {number} [endLevel]   - Last source octree level (direct mode)
 */

/**
 * Gets the point range for a specific LOD level
 *
 * Behavior depends on LOD_MAPPING_MODE:
 *
 * 'threshold' mode:
 *   These ranges define DISPLAY thresholds (when to switch LODs), NOT loading ranges.
 *   For loading, the actual range depends on what points were loaded by previous LODs.
 *   For example, if LOD 0 loads 1500 points (because it loads whole levels),
 *   then LOD 1 should load from 1500 (not 1000) to avoid duplicates.
 *   This is handled by the "start on next level" logic in RcsLodUtils.
 *
 * 'direct' mode:
 *   Maps LOD levels to source octree levels, with multiple levels combined into LOD 0.
 *   - LOD 0: combines DIRECT_MODE_LEVELS_IN_LOD0 coarsest levels (more efficient)
 *   - LOD 1+: one-to-one mapping with source levels
 *   Returns { startLevel, endLevel } defining which source levels to load.
 *
 * @param {number} lodLevel - The LOD level (0-based)
 * @returns {LodRange}
 */
export function getLodRange(lodLevel) {
  if (LOD_MAPPING_MODE === 'direct') {
    // Direct mapping with combined levels in LOD 0
    if (lodLevel === 0) {
      // LOD 0 combines multiple coarsest source levels
      return {
        startLevel: 0,
        endLevel: DIRECT_MODE_LEVELS_IN_LOD0 - 1
      };
    } else {
      // LOD 1+ map to individual source levels with offset
      const sourceLevel = DIRECT_MODE_LEVELS_IN_LOD0 + (lodLevel - 1);
      return {
        startLevel: sourceLevel,
        endLevel: sourceLevel
      };
    }
  } else {
    // Threshold mode: LOD levels based on cumulative point counts
    if (lodLevel < 0 || lodLevel >= RCS_LOD_THRESHOLDS.length) {
      throw new Error(`Invalid LOD level: ${lodLevel}. Must be 0-${RCS_LOD_THRESHOLDS.length - 1}`);
    }

    const startPoint = lodLevel === 0 ? 0 : RCS_LOD_THRESHOLDS[lodLevel - 1];
    const endPoint = RCS_LOD_THRESHOLDS[lodLevel];

    return { startPoint, endPoint };
  }
}

/**
 * Determines which LOD levels should be created for a given node
 *
 * 'threshold' mode: Based on total point count and thresholds
 * 'direct' mode: Based on source levels with points, accounting for DIRECT_MODE_LEVELS_IN_LOD0
 *
 * @param {number|Array<number>} totalPointsOrNumSvoLeafNodes - Total points (threshold mode) or per-level SVO leaf counts (direct mode)
 * @returns {number[]} Array of LOD level indices that should be created
 */
export function getRequiredLodLevels(totalPointsOrNumSvoLeafNodes) {
  const lodLevels = [];

  if (LOD_MAPPING_MODE === 'direct') {
    // Direct mode: create LODs based on which source levels have points
    const numSvoLeafNodes = Array.isArray(totalPointsOrNumSvoLeafNodes) ?
    totalPointsOrNumSvoLeafNodes :
    [];

    // Check if the node has any points at all
    let hasAnyPoints = false;
    for (let level = 0; level < numSvoLeafNodes.length; level++) {
      if (numSvoLeafNodes[level] > 0) {
        hasAnyPoints = true;
        break;
      }
    }

    // CRITICAL: LOD 0 must exist for every node that has any points at all
    // Otherwise we get holes in the point cloud when nodes have all their points
    // in higher levels (e.g., levels 6+) and LOD 0 is skipped.
    if (hasAnyPoints) {
      lodLevels.push(0);
    }

    // Check subsequent source levels for LOD 1+
    for (let level = DIRECT_MODE_LEVELS_IN_LOD0; level < numSvoLeafNodes.length; level++) {
      if (numSvoLeafNodes[level] > 0) {
        // Map source level to LOD level
        const lodLevel = 1 + (level - DIRECT_MODE_LEVELS_IN_LOD0);
        lodLevels.push(lodLevel);
      }
    }
  } else {
    // Threshold mode: based on cumulative point count
    const totalPoints = Array.isArray(totalPointsOrNumSvoLeafNodes) ?
    totalPointsOrNumSvoLeafNodes.reduce((sum, count) => sum + count, 0) :
    totalPointsOrNumSvoLeafNodes;

    for (let lod = 0; lod < RCS_LOD_THRESHOLDS.length; lod++) {
      const { startPoint } = getLodRange(lod);

      // Include this LOD if there are points beyond its start
      if (totalPoints > (/** @type {number} */startPoint)) {
        lodLevels.push(lod);
      } else {
        // No more LODs needed after this point
        break;
      }
    }
  }

  return lodLevels;
}